import React, { Fragment, useEffect, useState } from "react";
import { Accordion, Nav, Spinner } from "react-bootstrap";
import { BsRecord2 } from "react-icons/bs";
import { NavLink, useLocation } from "react-router-dom";
import SimpleBar from "simplebar-react";
import "simplebar-react/dist/simplebar.min.css";
import { findActiveDropdownId, findActiveSubDropdownId } from "../constants";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { selectUser, setUserPermission } from "../features/auth/authSlice";
import { getAllModule } from "../services/adminApi";

const JsSidebar = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { pathname } = useLocation();
  const [sidebarData, setSidebarData] = useState([]);
  const [loading, setLoading] = useState(true);

  // PERMISSION-DRIVEN MENU: the sidebar API returns view/create/update/delete
  // per node for the logged-in role — render ONLY what the role can view.
  // A module/sub-module with view=0 used to render anyway and its screen then
  // answered "PERMISSION DENIED!!"; now it simply doesn't appear. The FULL
  // (unfiltered) tree still goes to redux so screens keep their per-button
  // flag checks.
  // Menus that exist only as permission nodes for APIs used INSIDE another
  // screen. "Stock Punch" gates the Expense Punch tab of Stock Management, so
  // it must be grantable in the editor but never listed as its own menu
  // (client: "stock punch management hatana hi hai", 2026-09-07 / 2026-09-09).
  const HIDDEN_SIDEBAR_PATHS = ["/StockPunchManagement"];
  // checkPermission on the backend walks every URL segment, so an action on a
  // tab needs the right on the TAB row AND on its MENU row. Screens read
  // button flags from the tab row only, which showed buttons the API then
  // refused ("Access Denied", 2026-09-09). Fold the menu row's flags into each
  // tab row once, here, so every screen's buttons match what the API allows.
  const RIGHTS = ["view", "create", "update", "delete", "approve"];
  const foldMenuRights = (tree) =>
    (tree || []).map((m) => ({
      ...m,
      submodules: (m.submodules || []).map((s) => ({
        ...s,
        modulesOfSubModule: (s.modulesOfSubModule || []).map((l) => {
          const eff = { ...l };
          RIGHTS.forEach((k) => (eff[k] = Number(l[k]) > 0 && Number(s[k]) > 0 ? 1 : 0));
          // A sub-tab sits inside this tab, so it can never allow more than the
          // tab does — fold it the same way, one level further down.
          eff.subTabs = (l.subTabs || []).map((c) => {
            const sub = { ...c };
            RIGHTS.forEach((k) => (sub[k] = Number(c[k]) > 0 && Number(eff[k]) > 0 ? 1 : 0));
            return sub;
          });
          return eff;
        }),
      })),
    }));
  // A node that exists only to be granted, never navigated to: the explicit
  // list above, plus any ANCHOR path ("/dashboard#billing-chart"). Anchors are
  // the dashboard's per-heading permission nodes (2026-09-15) — see
  // utils/dashboardSections.js. They must stay out of the menu entirely.
  const isPermissionOnlyNode = (s) =>
    HIDDEN_SIDEBAR_PATHS.includes(s?.path) || String(s?.path || "").includes("#");

  const filterByView = (tree) =>
    (tree || [])
      .map((m) => {
        // Count only sub-menus that could ever BE a menu. Dashboard owns ten
        // permission-only section nodes; counting those made it look like a
        // dropdown, so it would vanish from the sidebar the moment a role had
        // no section granted, even though /dashboard itself was permitted.
        const hadSubmodules = (m.submodules || []).filter((s) => !isPermissionOnlyNode(s)).length > 0;
        const submodules = (m.submodules || [])
          .filter((s) => !isPermissionOnlyNode(s))
          .map((s) => ({
            ...s,
            modulesOfSubModule: (s.modulesOfSubModule || []).filter(
              (x) => Number(x.view) > 0,
            ),
          }))
          .filter((s) => Number(s.view) > 0);
        return { ...m, submodules, _hadSubmodules: hadSubmodules };
      })
      .filter(
        (m) =>
          Number(m.view) > 0 &&
          // a standalone module (no submodules at all, e.g. Dashboard) shows
          // on its own view flag; a dropdown module whose every sub-module is
          // hidden has nothing to open, so it hides too
          (!m._hadSubmodules || m.submodules.length > 0),
      );

  const fetchAllSidebarData = async () => {
    setLoading(true);
    const res = await getAllModule("CONTRACTOR");

    if (res.status) {
      setSidebarData(filterByView(res.data));
      dispatch(setUserPermission(foldMenuRights(res.data)));
    } else {
      setSidebarData([]);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchAllSidebarData();
  }, []);

  if (loading) {
    return (
      <div className="text-center mt-5">
        <Spinner animation="border" variant="secondary" size="sm" /> PLEASE
        WAIT...
      </div>
    );
  }

  // An unconfigured role used to spin forever here; say it plainly instead.
  if (sidebarData?.length == 0) {
    return (
      <div className="text-center mt-5 px-2 text-gray">
        No modules are assigned to your role. Please contact your
        administrator.
      </div>
    );
  }

  return (
    <section className="sidebar">
      <SimpleBar color="red" className="area">
        <Accordion
          defaultActiveKey={findActiveDropdownId(sidebarData, pathname) || 0}
        >
          <Nav className="d-grid gap-2 pe-3 ps-2 pt-3 mb-3">
            {sidebarData?.map((e) => (
              <Fragment key={e.id}>
                {e?.submodules?.length > 0 ? (
                  <Accordion.Item eventKey={e?.id}>
                    <Accordion.Header
                      title={e?.title}
                      className={
                        e?.status ? "active-module" : `un-active-module`
                      }
                    >
                      <div className="d-grid">
                        <div className="d-flex">
                          <img
                            className="me-2 object-fit"
                            width={20}
                            height={20}
                            src={`${process.env.REACT_APP_API_URL}${e.icon}`}
                          />
                          <div className="text-truncatee">{e?.title} </div>
                        </div>
                      </div>
                    </Accordion.Header>

                    <Accordion.Body className="last-child-none d-grid p-2">
                      <React.Fragment>
                        {e?.submodules?.map((body) => (
                          <Fragment key={body.id}>
                            {/* {body?.modulesOfSubModule?.length > 0 ? (
                              <Accordion
                                defaultActiveKey={
                                  findActiveSubDropdownId(body, pathname) || 0
                                }
                                className="my-1"
                              >
                                <Accordion.Item eventKey={body.id}>
                                  <Accordion.Header title={body.title}>
                                    <div className="d-grid">
                                      <div className="text-truncatee">
                                        {body.title}
                                      </div>
                                    </div>
                                  </Accordion.Header>
                                  <Accordion.Body className="last-child-none d-grid p-2">
                                    <React.Fragment>
                                      {body?.modulesOfSubModule?.map((bb) => (
                                        <NavLink
                                          // to={bb.status ? bb.path : "#"}
                                          to={bb.path}
                                          className="px-0 d-block hr-border2 py-2 text-gray text-truncatee text-decoration-none"
                                          key={bb.id}
                                          title={bb.title}
                                        >
                                          <BsRecord2 /> {bb.title}
                                        </NavLink>
                                      ))}
                                    </React.Fragment>
                                  </Accordion.Body>
                                </Accordion.Item>
                              </Accordion>
                            ) : (
                              <NavLink
                                // to={body.status ? body.path : "#"}
                                to={body.path}
                                className="px-0 d-block hr-border2 py-2 text-gray text-truncatee text-decoration-none"
                                title={body.title}
                              >
                                <BsRecord2 /> {body.title}
                              </NavLink>
                            )} */}
                            <NavLink
                              // to={body.status ? body.path : "#"}
                              to={body.path}
                              className="px-0 d-block hr-border2 py-2 text-gray text-truncatee text-decoration-none"
                              title={body.title}
                            >
                              <div className="d-flex align-items-center gap-1">
                                <BsRecord2 /> <div> {body.title} </div>
                              </div>
                            </NavLink>
                          </Fragment>
                        ))}
                      </React.Fragment>
                    </Accordion.Body>
                  </Accordion.Item>
                ) : (
                  <NavLink
                    // to={e.status ? e.path : "#"}
                    to={e.path}
                    className={`text-start my-bg-shadow r-5 text-gray text-truncatee text-decoration-none`}
                    style={{
                      padding: ".6rem .7rem",
                      borderLeft: e.status
                        ? "5px solid var(--text-green)"
                        : "5px solid var(--btn-danger1)",
                      backgroundColor: e.status ? "" : "#cfcfcf",
                      // pointerEvents: e.status ? "" : "none",
                      cursor: e.status ? "" : "no-drop",
                    }}
                    title={e.title}
                  >
                    <div className="d-flex">
                      <img
                        className="me-2 object-fit"
                        width={20}
                        height={20}
                        src={`${process.env.REACT_APP_API_URL}${e.icon}`}
                      />
                      {e.title}
                    </div>
                  </NavLink>
                )}
              </Fragment>
            ))}
          </Nav>
        </Accordion>
      </SimpleBar>
    </section>
  );
};

export default JsSidebar;